(() => {
  const SELECTOR = '[data-product-siblings-swatches]';
  const LINK_SELECTOR = '[data-product-sibling-link]';
  const REVIEWS_PDP_WIDGET_SELECTOR = ".yotpo-widget-instance[data-yotpo-instance-id='964925']";
  const PDP_YOTPO_SELECTOR = ".new-product .yotpo-widget-instance[data-yotpo-instance-id='1086524']";
  const PDP_YOTPO_BOTTOM_LINE_SELECTOR = '.yotpo-sr-bottom-line-text, .yotpo-sr-bottom-line-text--right-panel';
  const ANCHOR_TOLERANCE_PX = 1;
  const ANCHOR_RECHECK_DELAY_MS = 140;
  // Min slack required to count a swatch as "fitting"; if the last one only fits
  // by sub-pixels, we drop it to a clean page rather than show it clipped.
  const FIT_SAFETY_PX = 3;

  let activeRequestController = null;
  let requestSequence = 0;
  let initialized = false;

  const isLatestRequest = (requestId, controller) =>
    requestId === requestSequence && !controller.signal.aborted;

  // Alternate templates render a different main section, so an active ?view= has to
  // survive renders, swaps and fallbacks or the sibling lands on its default template.
  const withView = (href) => {
    const view = new URLSearchParams(window.location.search).get('view');
    if (!view) return href;
    const url = new URL(href, window.location.origin);
    url.searchParams.set('view', view);
    return url.toString();
  };

  const getRenderUrl = (href, sectionId) => {
    const url = new URL(withView(href), window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const getRenderedProductInfo = (htmlText, sectionId) => {
    const html = new DOMParser().parseFromString(htmlText, 'text/html');
    return html.querySelector(`#MainProduct-${sectionId}`) || html.querySelector("product-info[id^='MainProduct-']");
  };

  const getScrollStep = (container) => {
    const track = container.querySelector('.pdp-siblings__track');
    const firstLink = track?.children[0];
    if (!track || !firstLink) return 44;
    const trackStyle = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
    return firstLink.getBoundingClientRect().width + gap;
  };

  // Constrain the viewport to a whole number of swatches so a swatch is never
  // cut off at the right edge; the overflow stays reachable via scroll / nav.
  // The resulting page count is cached on the container for the nav + snapping.
  const fitViewportToWholeItems = (container) => {
    const viewport = container.querySelector('.pdp-siblings__viewport');
    const track = container.querySelector('.pdp-siblings__track');
    const firstItem = track?.children[0];
    if (!viewport || !track || !firstItem) return;

    // Measure from a known base state. `--compact` shrinks the viewport to its own
    // content width, so measuring while it is applied reads the content width as the
    // available space and under-counts by one swatch. Since updateButtons() applies
    // `--compact` right after this runs, every re-run (window load, a sibling swap,
    // a resize) would otherwise flip between "everything fits" and "cap it to 2",
    // hiding a swatch and toggling the arrows. Resetting first makes this idempotent.
    container.classList.remove('pdp-siblings--compact');
    viewport.style.maxWidth = '';

    const step = getScrollStep(container);
    if (step <= 0) return;

    const trackStyle = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
    const totalItems = track.children.length;

    // +gap because the last item in a row has no trailing gap; -FIT_SAFETY_PX so
    // a swatch that only fits by sub-pixels is dropped instead of clipped.
    const fitCount = Math.min(
      totalItems,
      Math.max(1, Math.floor((viewport.clientWidth + gap - FIT_SAFETY_PX) / step))
    );
    container.dataset.fitCount = String(fitCount);

    if (fitCount >= totalItems) return;

    // Round the width UP (plus 1px safety) so sub-pixel rounding of the width
    // and scroll position can never clip the last visible swatch's right edge.
    // The +1px stays well within the inter-swatch gap, so no extra swatch peeks.
    viewport.style.maxWidth = `${Math.ceil(fitCount * step - gap) + 1}px`;
  };

  const getSiblingLinks = (container) => Array.from(container.querySelectorAll(LINK_SELECTOR));

  const getActiveSiblingIndex = (links) =>
    links.findIndex((link) =>
      link.classList.contains('pdp-siblings__link--active') || link.getAttribute('aria-current') === 'page'
    );

  // Quantize the scroll to an exact swatch boundary so iOS (which ignores native
  // scroll-snap on programmatic scrolls) can never rest mid-swatch.
  const snapToPage = (container, pageStart, smooth) => {
    const viewport = container.querySelector('.pdp-siblings__viewport');
    if (!viewport) return;

    const step = getScrollStep(container);
    const totalItems = getSiblingLinks(container).length;
    const fitCount = Number.parseInt(container.dataset.fitCount, 10) || 1;
    const maxStart = Math.max(0, totalItems - fitCount);
    const start = Math.min(Math.max(0, pageStart), maxStart);

    viewport.scrollTo({ left: Math.round(start * step), behavior: smooth ? 'smooth' : 'auto' });
  };

  const getCurrentPageStart = (container) => {
    const viewport = container.querySelector('.pdp-siblings__viewport');
    const step = getScrollStep(container);
    if (!viewport || step <= 0) return 0;
    return Math.round(viewport.scrollLeft / step);
  };

  // Keep the selected swatch visible and aligned to a swatch boundary, so a
  // freshly rendered product never hides or half-shows its active swatch.
  const scrollActiveIntoView = (container) => {
    const links = getSiblingLinks(container);
    const activeIndex = getActiveSiblingIndex(links);
    if (activeIndex < 0) return;
    snapToPage(container, activeIndex, false);
  };

  const updateButtons = (container) => {
    const viewport = container.querySelector('.pdp-siblings__viewport');
    const track = container.querySelector('.pdp-siblings__track');
    const prevBtn = container.querySelector('.pdp-siblings__nav--prev');
    const nextBtn = container.querySelector('.pdp-siblings__nav--next');
    const siblingLinks = getSiblingLinks(container);
    const totalItems = siblingLinks.length;

    if (!viewport || !track || !prevBtn || !nextBtn) return;

    const hasOverflow = track.scrollWidth - viewport.clientWidth > 1;
    container.classList.toggle('pdp-siblings--compact', !hasOverflow);

    if (!hasOverflow) {
      viewport.scrollLeft = 0;
    }

    if (totalItems <= 1) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      prevBtn.setAttribute('aria-disabled', 'true');
      nextBtn.setAttribute('aria-disabled', 'true');
      return;
    }

    // Arrows page through the swatch list (scroll only); they never swap the
    // product, so disabling is driven purely by scroll position.
    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    prevBtn.disabled = viewport.scrollLeft <= 1;
    nextBtn.disabled = viewport.scrollLeft >= maxScroll - 1;

    prevBtn.setAttribute('aria-disabled', String(prevBtn.disabled));
    nextBtn.setAttribute('aria-disabled', String(nextBtn.disabled));
  };

  const setLoadingState = (container, isLoading) => {
    container.classList.toggle('pdp-siblings--loading', isLoading);
    if (isLoading) {
      container.setAttribute('aria-busy', 'true');
    } else {
      container.removeAttribute('aria-busy');
    }
  };

  const refreshYotpoWidgets = (scope = document) => {
    const hasWidget = !!scope.querySelector('.yotpo-widget-instance');
    if (!hasWidget) return;

    const maxRetries = 8;
    let attempts = 0;

    const tryRefresh = () => {
      attempts += 1;
      const ready = window.yotpo && typeof window.yotpo.refreshWidgets === 'function';

      if (ready) {
        window.yotpo.refreshWidgets();
        syncPdpYotpoReviewCount(scope);
        window.requestAnimationFrame(() => syncPdpYotpoReviewCount(scope));
        window.setTimeout(() => syncPdpYotpoReviewCount(scope), 280);
        return;
      }

      if (attempts < maxRetries) {
        window.setTimeout(tryRefresh, 200);
      }
    };

    tryRefresh();
  };

  const syncPdpYotpoReviewCount = (scope = document) => {
    const widgets = Array.from(scope.querySelectorAll(PDP_YOTPO_SELECTOR));

    widgets.forEach((widget) => {
      const bottomLineNodes = Array.from(widget.querySelectorAll(PDP_YOTPO_BOTTOM_LINE_SELECTOR));
      if (!bottomLineNodes.length) return;

      const preferredText = widget.querySelector('.yotpo-sr-bottom-line-text')?.textContent || '';
      const fallbackText = bottomLineNodes
        .map((node) => node.textContent || '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

      const textToParse = preferredText || fallbackText;
      const normalizedText = textToParse.toLowerCase();

      let reviewsCount = Number.NaN;

      const countWithLabelMatch = normalizedText.match(/(\d[\d\s.,]*)\s*(reviews?|rese(?:n|ñ)as?|opiniones?)/i);
      if (countWithLabelMatch) {
        const digitsOnly = countWithLabelMatch[1].replace(/\D/g, '');
        reviewsCount = digitsOnly ? Number.parseInt(digitsOnly, 10) : Number.NaN;
      } else if (/\bno\s+reviews\b|\bsin\s+rese(?:n|ñ)as\b/i.test(normalizedText)) {
        reviewsCount = 0;
      }

      const hasZeroReviews = Number.isFinite(reviewsCount) && reviewsCount === 0;

      widget.classList.toggle('yotpo-widget-instance--zero-reviews', hasZeroReviews);
    });
  };

  const applyProductSwap = (currentProductInfo, renderedProductInfo) => {
    if (typeof HTMLUpdateUtility !== 'undefined' && typeof HTMLUpdateUtility.viewTransition === 'function') {
      HTMLUpdateUtility.viewTransition(currentProductInfo, renderedProductInfo);
      return;
    }

    currentProductInfo.replaceWith(renderedProductInfo);
  };

  const refreshReviewsPdpSection = async (targetHref, signal) => {
    const reviewsWidget = document.querySelector(REVIEWS_PDP_WIDGET_SELECTOR);
    if (!reviewsWidget) return;

    const sectionElement = reviewsWidget.closest("[id^='shopify-section-']");
    if (!sectionElement?.id) return;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    if (!sectionId) return;

    const response = await fetch(getRenderUrl(targetHref, sectionId), {
      headers: {
        Accept: 'text/html',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`Reviews section render failed: ${response.status}`);
    }

    const htmlText = await response.text();
    const html = new DOMParser().parseFromString(htmlText, 'text/html');
    const renderedSection = html.getElementById(sectionElement.id);
    if (!renderedSection) return;

    sectionElement.innerHTML = renderedSection.innerHTML;
  };

  const keepSwatchesPosition = (sectionId, previousTop) => {
    const currentProductInfo = document.querySelector(`#MainProduct-${sectionId}`);
    if (!currentProductInfo) return;

    const nextContainer = currentProductInfo.querySelector(SELECTOR);
    if (!nextContainer) return;

    const nextTop = nextContainer.getBoundingClientRect().top;
    const topDelta = nextTop - previousTop;

    if (Math.abs(topDelta) > ANCHOR_TOLERANCE_PX) {
      window.scrollBy({ top: topDelta, left: 0, behavior: 'auto' });
    }
  };

  const stabilizeSwatchesPosition = (sectionId, previousTop) => {
    keepSwatchesPosition(sectionId, previousTop);

    // Re-check after late layout updates (e.g. media sizing) to avoid sticky gallery drift.
    window.requestAnimationFrame(() => {
      keepSwatchesPosition(sectionId, previousTop);
    });

    window.setTimeout(() => {
      keepSwatchesPosition(sectionId, previousTop);
    }, ANCHOR_RECHECK_DELAY_MS);
  };

  const swapProductInfo = async (link, container) => {
    const productInfo = container.closest('product-info');
    if (!productInfo) {
      window.location.href = withView(link.href);
      return;
    }

    const sectionId = productInfo.dataset.section || container.dataset.sectionId;
    if (!sectionId) {
      window.location.href = withView(link.href);
      return;
    }

    const targetUrl = new URL(link.href, window.location.origin);
    const currentUrl = new URL(window.location.href, window.location.origin);
    if (targetUrl.pathname === currentUrl.pathname && targetUrl.search === currentUrl.search) {
      return;
    }

    const swatchesTopBeforeSwap = container.getBoundingClientRect().top;

    const requestId = ++requestSequence;

    activeRequestController?.abort();
    const requestController = new AbortController();
    activeRequestController = requestController;

    setLoadingState(container, true);

    try {
      const response = await fetch(getRenderUrl(link.href, sectionId), {
        headers: {
          Accept: 'text/html',
        },
        signal: requestController.signal,
      });

      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

      const htmlText = await response.text();
      const renderedProductInfo = getRenderedProductInfo(htmlText, sectionId);
      if (!renderedProductInfo) throw new Error('Rendered product-info not found');

      if (!isLatestRequest(requestId, requestController)) return;

      applyProductSwap(productInfo, renderedProductInfo);
      // Re-anchor in the same task as the swap. The replaced markup is shorter until
      // its media and widgets mount, so the page shifts the moment it lands; waiting
      // until after the reviews fetch below would leave that shift on screen for as
      // long as the request takes (~245ms on mobile) and then snap it back.
      keepSwatchesPosition(sectionId, swatchesTopBeforeSwap);

      try {
        await refreshReviewsPdpSection(link.href, requestController.signal);
      } catch (refreshError) {
        console.warn('Reviews PDP section refresh skipped:', refreshError);
      }

      if (!isLatestRequest(requestId, requestController)) return;

      window.history.replaceState({}, '', withView(link.href));

      // Ensure new markup gets initialized when inserted without a full page load.
      window.requestAnimationFrame(() => {
        if (!isLatestRequest(requestId, requestController)) return;
        stabilizeSwatchesPosition(sectionId, swatchesTopBeforeSwap);
        initSwatches(document);
        refreshYotpoWidgets(document);
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Sibling product render failed:', error);
      window.location.href = withView(link.href);
    } finally {
      if (activeRequestController === requestController) {
        setLoadingState(container, false);
        activeRequestController = null;
      }
    }
  };

  const initSwatches = (root = document) => {
    const containers = Array.from(root.querySelectorAll(SELECTOR));
    containers.forEach((container) => {
      if (container.dataset.sliderInitialized === 'true') return;
      container.dataset.sliderInitialized = 'true';

      const viewport = container.querySelector('.pdp-siblings__viewport');
      const prevBtn = container.querySelector('.pdp-siblings__nav--prev');
      const nextBtn = container.querySelector('.pdp-siblings__nav--next');

      if (!viewport || !prevBtn || !nextBtn) return;

      const moveByPage = (direction) => {
        const fitCount = Number.parseInt(container.dataset.fitCount, 10) || 1;
        snapToPage(container, getCurrentPageStart(container) + fitCount * direction, true);
      };

      prevBtn.addEventListener('click', () => {
        moveByPage(-1);
      });

      nextBtn.addEventListener('click', () => {
        moveByPage(1);
      });

      // Re-align to a swatch boundary once touch/momentum scrolling settles, so
      // iOS can never leave the viewport resting mid-swatch.
      let settleTimer = null;
      viewport.addEventListener(
        'scroll',
        () => {
          updateButtons(container);
          window.clearTimeout(settleTimer);
          settleTimer = window.setTimeout(() => {
            const step = getScrollStep(container);
            const pageStart = getCurrentPageStart(container);
            // Skip when already aligned, otherwise the no-op scroll would loop.
            if (Math.abs(viewport.scrollLeft - Math.round(pageStart * step)) > 1) {
              snapToPage(container, pageStart, true);
            }
          }, 90);
        },
        { passive: true }
      );

      fitViewportToWholeItems(container);
      updateButtons(container);
      scrollActiveIntoView(container);
    });
  };

  const handleDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const link = event.target.closest(LINK_SELECTOR);
    if (!link) return;

    const container = link.closest(SELECTOR);
    if (!container) return;

    event.preventDefault();
    if (container.classList.contains('pdp-siblings--loading')) return;

    swapProductInfo(link, container);
  };

  const handleResize = () => {
    document.querySelectorAll(SELECTOR).forEach((container) => {
      fitViewportToWholeItems(container);
      updateButtons(container);
      scrollActiveIntoView(container);
    });
  };

  const boot = () => {
    initSwatches(document);
    syncPdpYotpoReviewCount(document);

    // The initial fit runs before the nav visibility and final column width settle,
    // which can under-count by one swatch. Re-fit once layout has settled.
    window.requestAnimationFrame(() => handleResize());

    if (initialized) return;
    initialized = true;

    document.addEventListener('click', handleDocumentClick);
    window.addEventListener('load', handleResize, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    document.addEventListener('shopify:section:load', (event) => {
      initSwatches(event.target);
      handleResize();
    });
    document.addEventListener('product-info:loaded', (event) => {
      initSwatches(event.target);
      handleResize();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
